import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`
});

// Add Authorization header to every request
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    console.warn('Token retrieval failed:', e);
  }
  return config;
});

// Automatic token refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const res = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken });
        if (res.data.access_token) {
          localStorage.setItem('access_token', res.data.access_token);
        }
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Transactions
export const getTransactions = (params = {}) => api.get('/transactions', { params });
export const createTransaction = (data) => api.post('/transactions', data);
export const getTransaction = (id) => api.get(`/transactions/${id}`);
export const updateTransaction = (id, data) => api.put(`/transactions/${id}`, data);
export const deleteTransaction = (id) => api.delete(`/transactions/${id}`);
export const analyzeTransaction = (id) => api.post(`/transactions/${id}/analyze`);
export const analyzeTransactionsBatch = () => api.post('/transactions/analyze-batch');

// Review Queue
export const getReviewQueue = () => api.get('/review-queue');
export const approveTransaction = (id, data) => api.post(`/review-queue/${id}/approve`, data);
export const rejectTransaction = (id, data) => api.post(`/review-queue/${id}/reject`, data);

// Accounts (Chart of Accounts)
export const getAccounts = () => api.get('/accounts');
export const createAccount = (data) => api.post('/accounts', data);
export const updateAccount = (id, data) => api.put(`/accounts/${id}`, data);
export const deleteAccount = (id) => api.delete(`/accounts/${id}`);

// Vendors
export const getVendors = () => api.get('/vendors');
export const createVendor = (data) => api.post('/vendors', data);
export const updateVendor = (id, data) => api.put(`/vendors/${id}`, data);

// Context Rules
export const getRules = () => api.get('/rules');
export const createRule = (data) => api.post('/rules', data);
export const updateRule = (id, data) => api.put(`/rules/${id}`, data);
export const deleteRule = (id) => api.delete(`/rules/${id}`);

// Corrections
export const getCorrections = (params = {}) => api.get('/corrections', { params });
export const createCorrection = (data) => api.post('/corrections', data);

// Client Profiles
export const getClients = () => api.get('/clients');
export const createClient = (data) => api.post('/clients', data);
export const getClient = (id) => api.get(`/clients/${id}`);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Company Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getRecentActivity = (limit = 10) => api.get('/dashboard/recent-activity', { params: { limit } });
export const getChartVolume = (params = {}) => api.get('/dashboard/chart-volume', { params });
export const getChartClassification = (params = {}) => api.get('/dashboard/chart-classification', { params });
export const getChartConfidence = (params = {}) => api.get('/dashboard/chart-confidence', { params });

// Audit Trail
export const getAuditTrail = (params = {}) => api.get('/audit-trail', { params });
export const getAuditStats = () => api.get('/audit-trail/stats');
export const exportAuditPdf = (params = {}) => api.get('/audit-trail/export-pdf', { params, responseType: 'blob' });

// Confidence Trend
export const getConfidenceTrend = () => api.get('/dashboard/chart-confidence-trend');

// Budgets
export const getBudgets = (year = 2026) => api.get('/budgets', { params: { year } });
export const createBudget = (data) => api.post('/budgets', data);
export const deleteBudget = (id) => api.delete(`/budgets/${id}`);
export const getBudgetComparison = (year = 2026) => api.get('/budgets/comparison', { params: { year } });

// Recurring
export const detectRecurring = () => api.get('/recurring/detect');
export const getManagedRecurring = () => api.get('/recurring');
export const createRecurring = (data) => api.post('/recurring', data);
export const deleteRecurring = (id) => api.delete(`/recurring/${id}`);

// Client Health
export const getClientHealth = (clientId) => api.get(`/clients/${clientId}/health`);
export const getClientsHealthSummary = () => api.get('/clients/health-summary');

// Journal Entries
export const getJournalEntries = (params = {}) => api.get('/journal-entries', { params });
export const createJournalEntry = (data) => api.post('/journal-entries', data);
export const deleteJournalEntry = (id) => api.delete(`/journal-entries/${id}`);

// Client Members
export const getClientMembers = (clientId) => api.get(`/clients/${clientId}/members`);
export const inviteClientMember = (clientId, data) => api.post(`/clients/${clientId}/members`, data);
export const removeClientMember = (clientId, memberId) => api.delete(`/clients/${clientId}/members/${memberId}`);

// Financial Statements
export const getPnL = (params = {}) => api.get('/reports/pnl', { params });
export const getBalanceSheet = (params = {}) => api.get('/reports/balance-sheet', { params });
export const exportPnLPdf = (params = {}) => api.get('/reports/pnl/pdf', { params, responseType: 'blob' });
export const exportBalanceSheetPdf = (params = {}) => api.get('/reports/balance-sheet/pdf', { params, responseType: 'blob' });
export const exportTrialBalancePdf = (params = {}) => api.get('/reports/trial-balance/pdf', { params, responseType: 'blob' });

// Reconciliation
export const uploadBankStatement = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/reconciliation/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const getReconSessions = () => api.get('/reconciliation/sessions');
export const getReconSession = (id) => api.get(`/reconciliation/sessions/${id}`);
export const autoMatchSession = (id) => api.post(`/reconciliation/sessions/${id}/auto-match`);
export const reconcileLine = (sessionId, data) => api.post(`/reconciliation/sessions/${sessionId}/reconcile`, data);

// Onboarding
export const getProfileCompleteness = (clientId) => api.get(`/clients/${clientId}/completeness`);

// Support Chat
export const chatAiAssistant = (data) => api.post('/chat/ai-assistant', data);
export const chatSales = (data) => api.post('/chat/sales', data);
export const getWhySnugglemind = () => api.get('/chat/why-snugglemind');
export const getSalesMessages = () => api.get('/chat/sales/messages');
export const replySalesMessage = (msgId, data) => api.post(`/chat/sales/${msgId}/reply`, data);
export const getSalesReplies = (msgId) => api.get(`/chat/sales/${msgId}/replies`);
export const getUnreadSalesCount = () => api.get('/chat/sales/unread-count');
export const updateSalesStatus = (msgId, data) => api.put(`/chat/sales/${msgId}/status`, data);

// Chat History
export const getChatHistory = (sessionId) => api.get(`/chat/history/${sessionId}`);
export const saveChatMessage = (data) => api.post('/chat/history', data);

// Proactive Suggestions
export const getSuggestions = () => api.get('/suggestions');

// Bank Feeds
export const connectBank = () => api.post('/bank-feeds/connect');
export const getBankConnections = () => api.get('/bank-feeds/connections');
export const syncBankTransactions = (connId) => api.post(`/bank-feeds/sync/${connId}`);
export const disconnectBank = (connId) => api.delete(`/bank-feeds/connections/${connId}`);

// Trial Balance
export const getTrialBalance = (params = {}) => api.get('/reports/trial-balance', { params });

// Multi-Currency
export const getCurrencyRates = (base = 'USD') => api.get('/currency/rates', { params: { base } });
export const convertCurrency = (data) => api.post('/currency/convert', data);
export const getSupportedCurrencies = () => api.get('/currency/supported');

// File Upload
export const uploadTransactions = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/transactions', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Subscription
export const getSubscriptionStatus = () => api.get('/subscription/status');
export const getSubscriptionPlans = () => api.get('/subscription/plans');
export const createCheckoutSession = (data) => api.post('/subscription/checkout', data);
export const getCheckoutStatus = (sessionId) => api.get(`/subscription/checkout-status/${sessionId}`);

// MFA
export const verifyMfa = (data) => api.post('/auth/mfa/verify', data);
export const resendMfa = (data) => api.post('/auth/mfa/resend', data);
export const getMfaStatus = () => api.get('/auth/mfa/status');
export const setupMfa = (data) => api.post('/auth/mfa/setup', data);
export const confirmMfaSetup = (data) => api.post('/auth/mfa/setup/confirm', data);
export const disableMfa = (data) => api.post('/auth/mfa/disable', data);

// Reconciliation Center
export const getReconOverview = () => api.get('/recon-center/overview');
export const uploadReconStatement = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/recon-center/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const setReconSessionType = (sessionId, data) => api.post(`/recon-center/sessions/${sessionId}/set-type`, data);
export const computeReconBalance = (sessionId) => api.post(`/recon-center/sessions/${sessionId}/compute-balance`);
export const dismissDuplicate = (txnId) => api.post(`/recon-center/duplicates/${txnId}/dismiss`);
export const removeDuplicate = (txnId) => api.post(`/recon-center/duplicates/${txnId}/remove`);

// Close Readiness
export const getCloseReadiness = (period) => api.get('/close-readiness', { params: period ? { period } : {} });
export const markPeriodClosed = (data) => api.post('/close-readiness/mark-closed', data);

// Exception Queue
export const getExceptionQueue = (category) => api.get('/exception-queue', { params: category ? { category } : {} });
export const resolveException = (txnId, data) => api.post(`/exception-queue/${txnId}/resolve`, data);
export const linkDocument = (txnId, data) => api.post(`/exception-queue/${txnId}/link-document`, data);

// Credit Card Controls
export const getCcOverview = () => api.get('/credit-card/overview');
export const flagCcTransaction = (txnId, data) => api.post(`/credit-card/${txnId}/flag`, data);
export const tagCcTransaction = (txnId, data) => api.post(`/credit-card/${txnId}/tag`, data);

// Missing Documents
export const getMissingDocuments = (period) => api.get('/documents/missing', { params: period ? { period } : {} });
export const sendDocReminder = (txnId, data) => api.post(`/documents/${txnId}/send-reminder`, data);
export const linkDocToTxn = (txnId, data) => api.post(`/documents/${txnId}/link`, data);
export const getDocReminders = () => api.get('/documents/reminders');

// Real-Time Alerts Center
export const getAlerts = (params = {}) => api.get('/alerts', { params });
export const getAlertsUnreadCount = () => api.get('/alerts/unread-count');
export const markAlertRead = (alertId) => api.post(`/alerts/${alertId}/read`);
export const snoozeAlert = (alertId) => api.post(`/alerts/${alertId}/snooze`);
export const markAllAlertsRead = () => api.post('/alerts/mark-all-read');

// Migration Importer (Botkeeper / generic CSV)
export const previewMigration = (formData) => api.post('/migration/preview', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const commitMigration = (data) => api.post('/migration/commit', data);
export const getMigrationHistory = () => api.get('/migration/history');

export default api;
